import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./importtagsmodal.module.scss";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
} from "reactstrap";
import ArrowDown from "../../../../assets/images/arrow-down-white.svg";
import CrossIcon from "../../../../assets/images/cross-icon.svg";
import ImageUploadIcon from "../../../../assets/images/documentupload-icon.svg";
import SellerIcon from "../../../../assets/images/seller-icon-white.svg";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import Button from "../../../Shared/Button";
import { importCsvTags } from "../../../../helpers/service";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit

const ImportTagsModal = ({ openModal, toggle, sellerData, setLoading }) => {
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [openBrandDropdown, setOpenBrandDropdown] = useState(false);
  const userData = useSelector((state) => state.authReducer);

  const initialState = {
    seller_id: "",
    file: null,
  };

  const formik = useFormik({
    initialValues: initialState,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const validateAndSetFile = (uploadedFile) => {
    if (!uploadedFile) {
      toast.warning("No file provided.");
      return;
    }

    const fileExtension = uploadedFile.name.split(".").pop();
    if (fileExtension.toLowerCase() !== "csv") {
      toast.warning("Only CSV files are allowed.");
      return;
    }

    if (uploadedFile.size > MAX_FILE_SIZE) {
      toast.warning("File size exceeds the 5MB limit.");
      return;
    }

    setFile(uploadedFile);
    formik.setFieldValue("file", uploadedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    validateAndSetFile(selectedFile);
  };

  const handleSubmit = async (values) => {
    if (!file) {
      toast.error("Please upload a CSV file.");
      return;
    }
    if (!values.seller_id) {
      toast.error("Please select a seller.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("seller_id", values.seller_id.toString());

      const response = await importCsvTags(formData, userData?.idToken);

      if (!response || response.error || response.success === false) {
        const errorMessage = response?.error?.message || response?.message || "Failed to import CSV";
        toast.success("Import Error: " + errorMessage);
        setLoading(false);
        throw new Error(errorMessage);
      }

      toast.success("Tags imported successfully.");
      setLoading(false);
      formik.resetForm();
      setFile(null);
      toggle();
    } catch (error) {
      setLoading(false);
      toast.error(error.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={openModal}
      toggle={toggle}
      centered
      fullscreen="sm"
      contentClassName={styles["modal_content"]}
      className={styles["container"]}
      wrapClassName={styles["modal_wrapper"]}
    >
      <div className={styles["managetags_modal_container"]}>
        <div>
          <p className={styles["header_text"]}>Import Tags</p>
        </div>

        <div className={styles["dropdowns_container"]}>
          <Dropdown
            isOpen={openBrandDropdown}
            toggle={() => setOpenBrandDropdown(!openBrandDropdown)}
          >
            <DropdownToggle className={styles["dropdown_toggle"]}>
              <img
                src={SellerIcon || "/placeholder.svg"}
                alt="sort"
                width={24}
                height={24}
              />
              <p className={styles["filterLabel"]}>
                {formik.values?.seller || "Sellers"}
              </p>
              <img
                src={ArrowDown || "/placeholder.svg"}
                alt="icon"
                width={32}
                height={32}
              />
            </DropdownToggle>
            <DropdownMenu className={styles["dropdown_menu"]}>
              {(sellerData || []).map((seller, index) => (
                <DropdownItem
                  key={index}
                  onClick={() =>
                    formik.setValues({
                      ...formik.values,
                      seller: seller?.name,
                      seller_id: seller?.id,
                    })
                  }
                >
                  {seller?.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>

        <div
          className={styles["file_upload_container"]}
          onClick={() => inputRef.current.click()}
          role="button"
          tabIndex={0}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <img
            src={ImageUploadIcon || "/placeholder.svg"}
            alt="Upload Icon"
            width={24}
            height={24}
          />
          <p className={styles["upload_text"]}>
            Document Upload (Drag and drop CSV)
          </p>
        </div>
        <input
          type="file"
          accept=".csv"
          ref={inputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {file && (
          <div className={styles["file_info_container"]}>
            <div className={styles["file_name_container"]}>
              <img
                src={ImageUploadIcon || "/placeholder.svg"}
                alt="File Icon"
                width={29}
                height={29}
              />
              <p className={styles["file_name_text"]}>{file.name}</p>
            </div>
            <img
              src={CrossIcon || "/placeholder.svg"}
              alt="Remove"
              width={13}
              height={13}
              role="button"
              onClick={() => {
                setFile(null);
                formik.setFieldValue("file", null);
              }}
            />
          </div>
        )}

        <div className={styles["submit_btns"]}>
          <Button
            className={styles["button_style_cancel"]}
            onClick={toggle}
            text="Cancel"
          />
          <Button
            className={styles["button_style_add"]}
            onClick={() => formik.submitForm()}
            text="Import"
          />
        </div>
      </div>
    </Modal>
  );
};

ImportTagsModal.propTypes = {
  openModal: PropTypes.bool,
  toggle: PropTypes.func,
  sellerData: PropTypes.array,
  setLoading: PropTypes.func,
};

export default ImportTagsModal;
